/*
  Cart page: the shipping estimate in the totals box and the suggested
  products under the cart.

  Changing the cart is the cart script's job (kv-cart-behavior); after every
  change it draws the inside of #kv-cart-main-root again. This file watches
  that element and sets its own parts up again each time, so it never needs to
  know which change happened or who made it.

  Shipping estimate. Shopify works out rates in two steps: the address is sent
  to /cart/prepare_shipping_rates.json, then /cart/async_shipping_rates.json is
  asked until the rates are ready. The second step always answers for the last
  address prepared, whatever it is asked, so estimates run one at a time and
  one that a newer estimate replaces stops early. The cheapest rate is shown
  and added to the total; with no rate for the address the row keeps the
  section's own text and the total leaves shipping out. The first address
  tried is the one the shopper entered before, else the country of their
  store localization. An address Shopify does not accept, or cannot check, is
  not kept, and the form says why.

  Suggested products. Shopify's recommendations for the first products in the
  cart are asked for from the cart page section itself, which answers with the
  finished row of cards, products already in the cart left out. With nothing
  suggested, the collection row the section printed is used, and with none
  the block stays hidden. A collection row is rebuilt from the section's copy
  whenever the products in the cart change, so a product just added leaves
  it.
*/

(() => {
  if (window.KvCartPage) return;

  const ROOT_ID = 'kv-cart-main-root';
  const ADDRESS_KEY = 'kv-cart-shipping-address';
  const POLL_TRIES = 8;

  const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /* ---------- Money ---------- */

  // The store's format is HTML, such as "&euro;{{amount}}". Tags are dropped
  // and entities turned into their characters, since the result is written
  // as text.
  function plainText(html) {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = html.replace(/<[^>]*>/g, '');
    return decoder.value;
  }

  function formatMoney(cents, format) {
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const template = plainText(String(format || '${{amount}}'));
    const match = template.match(pattern);
    if (!match) return template;

    const group = (value, decimals, thousands, decimal) => {
      const fixed = (Math.abs(value) / 100).toFixed(decimals);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return (value < 0 ? '-' : '') + (parts[1] ? whole + decimal + parts[1] : whole);
    };

    const value = Math.round(Number(cents) || 0);
    const styles = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_space_separator: [2, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.'],
    };
    const style = styles[match[1]] || styles.amount;
    return template.replace(pattern, group(value, style[0], style[1], style[2]));
  }

  /* ---------- Shipping estimate ---------- */

  let queue = Promise.resolve();
  let latest = 0;
  let latestRun = null;
  // The address last sent from the form, kept while its estimate runs so a
  // redraw meanwhile estimates it too rather than the address before it.
  let wanted = null;

  function savedAddress() {
    try {
      const value = JSON.parse(window.localStorage.getItem(ADDRESS_KEY));
      return value && value.country ? value : null;
    } catch (error) {
      return null;
    }
  }

  function saveAddress(address) {
    try {
      window.localStorage.setItem(ADDRESS_KEY, JSON.stringify(address));
    } catch (error) {
      /* Private browsing: the address is simply asked for again next time. */
    }
  }

  // Without an address from the shopper, their store country is tried: by the
  // name the country list uses, or by its code where the name is translated
  // and matches no entry, since Shopify only takes the English name or code.
  function defaultAddress(totals) {
    const known = wanted || savedAddress();
    if (known) return known;
    const select = totals.querySelector('select[name="country"]');
    if (!select) return null;
    const name = select.getAttribute('data-default');
    const code = select.getAttribute('data-default-code');
    const listed = Array.from(select.options).some((option) => option.value === name);
    const country = listed ? name : code || name;
    return country ? { country, province: '', zip: '' } : null;
  }

  const sameAddress = (a, b) =>
    Boolean(a && b) && a.country === b.country && a.province === b.province && a.zip === b.zip;

  // A validation answer lists its reasons per field; any other failure has one
  // general reason.
  class FieldErrors extends Error {
    constructor(fields) {
      super('address');
      this.fields = fields || {};
    }
  }

  async function prepare(address) {
    const response = await fetch(routeRoot() + 'cart/prepare_shipping_rates.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ shipping_address: address }),
    });
    if (response.status === 422) throw new FieldErrors(await response.json().catch(() => ({})));
    if (!response.ok) throw new Error('prepare ' + response.status);
  }

  // Right after a new address is prepared, the answer can still be the one for
  // the address before it. The rates are taken once two answers in a row
  // agree, which a stale answer followed by the real one never does. Asking
  // stops, with null, as soon as a newer estimate has been asked for.
  async function ratesWhenReady(replaced) {
    let delay = 400;
    let previous = null;
    let last = [];
    await wait(delay);
    for (let attempt = 0; attempt < POLL_TRIES; attempt += 1) {
      if (replaced()) return null;
      const response = await fetch(routeRoot() + 'cart/async_shipping_rates.json', {
        headers: { Accept: 'application/json' },
      });
      if (response.status === 422) throw new FieldErrors(await response.json().catch(() => ({})));
      const body = response.status === 200 ? await response.json().catch(() => null) : null;
      if (body && Array.isArray(body.shipping_rates)) {
        const answer = JSON.stringify(body.shipping_rates.map((rate) => [rate.name, rate.price]));
        if (answer === previous) return body.shipping_rates;
        previous = answer;
        last = body.shipping_rates;
      }
      await wait(delay);
      delay = Math.min(delay * 1.5, 2000);
    }
    return last;
  }

  function cheapest(rates) {
    return rates
      .map((rate) => ({ rate, cents: Math.round(parseFloat(rate.price) * 100) }))
      .filter((entry) => Number.isFinite(entry.cents))
      .sort((a, b) => a.cents - b.cents)[0];
  }

  function showRate(totals, best) {
    const row = totals.querySelector('[data-kv-cart-rate]');
    const total = totals.querySelector('[data-kv-cart-grand-total]');
    const format = totals.getAttribute('data-money-format');
    const base = Number(total && total.getAttribute('data-base')) || 0;

    if (row) {
      row.textContent = '';
      if (best) {
        row.append((best.rate.presentment_name || best.rate.name) + ': ');
        const price = document.createElement('span');
        price.className = 'kv-cart-price';
        price.textContent = formatMoney(best.cents, format);
        row.append(price);
      } else {
        row.textContent = totals.getAttribute('data-fallback') || '';
      }
    }
    if (total) total.textContent = formatMoney(base + (best ? best.cents : 0), format);
  }

  function fieldMessage(error) {
    return Object.keys(error.fields)
      .map((field) => [].concat(error.fields[field]).join(' '))
      .join(' ');
  }

  const currentTotals = () => document.querySelector('[data-kv-cart-estimate]');

  // Runs after any estimate already under way. One that a newer estimate has
  // replaced stops at its next step and shows nothing, so only the newest
  // reaches the page and none waits behind requests nobody needs. Resolves to
  // how it ended: 'shown'; 'invalid', with the reasons for the address form;
  // 'failed', when Shopify gave no usable answer; or 'replaced'.
  function estimate(address) {
    const id = ++latest;
    const replaced = () => id !== latest;
    const task = async () => {
      if (replaced()) return { status: 'replaced' };
      if (!currentTotals() || !address) return { status: 'shown' };
      try {
        await prepare(address);
        if (replaced()) return { status: 'replaced' };
        const rates = await ratesWhenReady(replaced);
        if (!rates || replaced()) return { status: 'replaced' };
        const totals = currentTotals();
        if (totals) showRate(totals, cheapest(rates));
        return { status: 'shown' };
      } catch (error) {
        if (replaced()) return { status: 'replaced' };
        const totals = currentTotals();
        if (totals) showRate(totals, null);
        return error instanceof FieldErrors
          ? { status: 'invalid', message: fieldMessage(error) }
          : { status: 'failed' };
      }
    };
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    latestRun = { address, run };
    return run;
  }

  // How the estimate of an address sent from the form ended. A redraw during
  // it starts a newer estimate of the same address, whose outcome then counts.
  async function outcomeFor(address) {
    let result = await estimate(address);
    while (result.status === 'replaced' && latestRun && sameAddress(latestRun.address, address)) {
      result = await latestRun.run;
    }
    return result;
  }

  // Fills a freshly drawn address form with the address used before, and
  // starts the base theme's country and province helper on it, which fills
  // the provinces of the chosen country and hides the province field for a
  // country without.
  function setUpAddressForm(totals) {
    const form = totals.querySelector('[data-kv-cart-address]');
    if (!form || form.dataset.kvReady) return;
    form.dataset.kvReady = 'true';

    const country = form.querySelector('select[name="country"]');
    const province = form.querySelector('select[name="province"]');
    const zip = form.querySelector('input[name="zip"]');
    const provinceField = province && province.closest('.kv-cart-address__field');

    const known = wanted || savedAddress();
    if (known) {
      country.setAttribute('data-default', known.country);
      province.setAttribute('data-default', known.province || '');
      zip.value = known.zip || '';
    }

    if (window.Shopify && typeof window.Shopify.CountryProvinceSelector === 'function' && provinceField) {
      new window.Shopify.CountryProvinceSelector(country.id, province.id, { hideElement: provinceField.id });
    }
  }

  // Puts an address back into a form, for a form drawn again while its
  // address was being checked. The change event lets the helper fill the
  // provinces of the country first.
  function fillForm(form, address) {
    const country = form.querySelector('select[name="country"]');
    const province = form.querySelector('select[name="province"]');
    const zip = form.querySelector('input[name="zip"]');
    country.value = address.country;
    country.dispatchEvent(new Event('change'));
    if (province) province.value = address.province;
    zip.value = address.zip;
  }

  // The link and the form are answered from the document, so a form drawn a
  // moment ago works before it has been set up, and never submits the page.
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-kv-cart-address-toggle]');
    if (!toggle) return;
    const form = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!form) return;
    const opening = form.hidden;
    form.hidden = !opening;
    toggle.setAttribute('aria-expanded', String(opening));
    if (opening) form.querySelector('select[name="country"]').focus();
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-kv-cart-address]');
    if (!form) return;
    event.preventDefault();

    const country = form.querySelector('select[name="country"]');
    const province = form.querySelector('select[name="province"]');
    const provinceField = province && province.closest('.kv-cart-address__field');
    const zip = form.querySelector('input[name="zip"]');
    const message = form.querySelector('[data-kv-cart-address-error]');
    const submit = form.querySelector('[type="submit"]');
    const address = {
      country: country.value,
      province: province && !(provinceField && provinceField.style.display === 'none') ? province.value : '',
      zip: zip.value.trim(),
    };

    if (submit) submit.setAttribute('aria-busy', 'true');
    message.textContent = '';
    const before = wanted;
    wanted = address;
    const result = await outcomeFor(address);
    if (submit) submit.removeAttribute('aria-busy');

    // Another address sent from the form meanwhile has taken over.
    if (result.status === 'replaced') return;

    // A redraw may have replaced the form meanwhile, so the form on the page
    // now is the one answered.
    const shown = document.getElementById(form.id) || form;
    const toggle = document.querySelector('[aria-controls="' + form.id + '"]');

    if (result.status === 'invalid' || result.status === 'failed') {
      // The address is not used. The form stays open with what was typed and
      // the reason, and the row goes back to the address used before.
      wanted = before;
      if (shown !== form) {
        fillForm(shown, address);
        shown.hidden = false;
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
      }
      const reason = result.status === 'invalid' ? result.message : '';
      shown.querySelector('[data-kv-cart-address-error]').textContent = reason || form.getAttribute('data-failed') || '';
      const totals = currentTotals();
      if (result.status === 'invalid' && totals) estimate(defaultAddress(totals));
      return;
    }

    saveAddress(address);
    shown.hidden = true;
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      const active = document.activeElement;
      if (!active || active === document.body || form.contains(active)) toggle.focus();
    }
  });

  function setUpEstimate() {
    const totals = document.querySelector('[data-kv-cart-estimate]');
    if (!totals) return;
    setUpAddressForm(totals);
    estimate(defaultAddress(totals));
  }

  /* ---------- Suggested products ---------- */

  let recsRun = 0;

  async function rowFor(box, productId, inCart) {
    const limit = Math.min(10, (Number(box.dataset.limit) || 6) + inCart);
    const url =
      box.dataset.url +
      '?product_id=' + encodeURIComponent(productId) +
      '&limit=' + limit +
      '&intent=related' +
      '&section_id=' + encodeURIComponent(box.dataset.sectionId);
    const response = await fetch(url);
    if (!response.ok) return null;
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-kv-cart-recs-row]');
  }

  // The collection row as the section printed it, less the products that are
  // in the cart now. Null when none is left.
  function collectionRow(inCart) {
    const template = document.querySelector('template[data-kv-cart-recs-fallback]');
    const source = template && template.content.querySelector('[data-kv-cart-recs-row]');
    if (!source) return null;
    const row = source.cloneNode(true);
    row.querySelectorAll('[data-kv-cart-recs-product]').forEach((item) => {
      if (inCart.includes(item.getAttribute('data-kv-cart-recs-product'))) item.remove();
    });
    return row.querySelector('[data-kv-cart-recs-product]') ? row : null;
  }

  function showRow(box, row) {
    if (!row) {
      box.hidden = true;
      box.replaceChildren();
      return;
    }
    box.replaceChildren(document.importNode(row, true));
    box.hidden = false;
    if (window.kvCarousels && typeof window.kvCarousels.start === 'function') window.kvCarousels.start(box);
  }

  // Runs again whenever the products in the cart change. A collection row
  // printed with the page already leaves out the cart's products, so it is
  // only rebuilt after a change.
  async function setUpRecommendations() {
    const box = document.querySelector('[data-kv-cart-recs]');
    const page = document.querySelector('[data-kv-cart-page]');
    if (!box || !page) return;

    const ids = (page.dataset.productIds || '').split(',').filter(Boolean);
    const key = ids.join(',');
    if (box.dataset.loadedFor === key) return;
    const firstLook = box.dataset.loadedFor === undefined;
    box.dataset.loadedFor = key;
    const run = ++recsRun;

    if (box.dataset.source === 'collection') {
      if (!firstLook) showRow(box, collectionRow(ids));
      return;
    }

    let row = null;
    for (const id of ids.slice(0, 3)) {
      try {
        row = await rowFor(box, id, ids.length);
      } catch (error) {
        row = null;
      }
      if (run !== recsRun) return;
      if (row) break;
    }
    showRow(box, row || collectionRow(ids));
  }

  /* ---------- Wiring ---------- */

  let pending = 0;
  function afterRedraw() {
    clearTimeout(pending);
    pending = setTimeout(() => {
      setUpEstimate();
      setUpRecommendations();
    }, 60);
  }

  let observer = null;
  function watchRoot() {
    if (observer) observer.disconnect();
    const root = document.getElementById(ROOT_ID);
    if (!root) return;
    observer = new MutationObserver(afterRedraw);
    observer.observe(root, { childList: true });
  }

  function start() {
    watchRoot();
    setUpEstimate();
    setUpRecommendations();
  }

  // The theme editor replaces the whole section when a setting changes.
  document.addEventListener('shopify:section:load', start);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.KvCartPage = { estimate, formatMoney, refresh: start };
})();
